import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FieldDevicePhotoResolutionModel, FieldDeviceSettingsModel } from 'models';
import { fieldDeviceSettingsService } from 'services';

export const fieldDeviceSettingsKeys = {
  all: [{ scope: 'fieldDeviceSettings' }] as const,
  detail: () => [{ ...fieldDeviceSettingsKeys.all[0], entity: 'detail' }] as const,
  photoResolutionOptions: () =>
    [{ ...fieldDeviceSettingsKeys.all[0], entity: 'photoResolutionOptions' }] as const,
};

const RESOLUTION_SEPARATOR = 'x';

export const toResolutionOption = ({ width, height }: FieldDevicePhotoResolutionModel) =>
  `${width}${RESOLUTION_SEPARATOR}${height}`;

export const toResolution = (option: string): FieldDevicePhotoResolutionModel => {
  const [width, height] = option.split(RESOLUTION_SEPARATOR).map(Number);
  return { width, height };
};

const toResolutionOptions = (resolutions: FieldDevicePhotoResolutionModel[]) =>
  resolutions.map(toResolutionOption);

const fetchFieldDeviceSettings = async () => fieldDeviceSettingsService.getFieldDeviceSettings();

const fetchPhotoResolutionOptions = async () =>
  fieldDeviceSettingsService.getPhotoResolutionOptions();

export const useFieldDeviceSettings = () => {
  return useQuery({
    queryKey: fieldDeviceSettingsKeys.detail(),
    queryFn: fetchFieldDeviceSettings,
  });
};

export const usePhotoResolutionOptions = () => {
  return useQuery({
    queryKey: fieldDeviceSettingsKeys.photoResolutionOptions(),
    queryFn: fetchPhotoResolutionOptions,
    select: toResolutionOptions,
    staleTime: Infinity,
  });
};

export const useUpdateFieldDeviceSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FieldDeviceSettingsModel) =>
      fieldDeviceSettingsService.updateFieldDeviceSettings(data),
    onSuccess: (result) => {
      queryClient.setQueryData(fieldDeviceSettingsKeys.detail(), result);
    },
  });
};
